import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, TrendingUp, TrendingDown, AlertTriangle, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatarHoras = (horas) => {
  if (horas === null || horas === undefined) return '00:00';
  const negativo = horas < 0;
  const abs = Math.abs(horas);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${negativo ? '-' : ''}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const BancoHorasDashboard = ({ empresas, empresaFiltro }) => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDashboard();
  }, [empresaFiltro]);

  const carregarDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (empresaFiltro) params.empresa_id = empresaFiltro;
      const response = await axios.get(`${API}/api/banco-horas/dashboard`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          <Clock className="mx-auto h-12 w-12 text-gray-300 mb-2" />
          <p>Dashboard indisponível. Nenhum funcionário com banco de horas configurado.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Funcionários no BH</p>
                <p className="text-2xl font-bold">{dashboard.total_funcionarios || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Saldo Geral</p>
                <p className={`text-2xl font-bold ${(dashboard.saldo_geral_horas || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatarHoras(dashboard.saldo_geral_horas)}
                </p>
              </div>
              {(dashboard.saldo_geral_horas || 0) >= 0
                ? <TrendingUp className="h-8 w-8 text-green-500" />
                : <TrendingDown className="h-8 w-8 text-red-500" />
              }
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Crédito Total</p>
                <p className="text-2xl font-bold text-green-600">{formatarHoras(dashboard.total_credito)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
            <div className="mt-2">
              <p className="text-sm text-gray-500">Débito Total</p>
              <p className="text-xl font-bold text-red-600">{formatarHoras(dashboard.total_debito)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">No Limite</p>
                <p className="text-2xl font-bold text-amber-600">{dashboard.funcionarios_no_limite || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {dashboard.alertas && dashboard.alertas.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-800 text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Alertas de Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboard.alertas.map((alerta, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <span className="text-amber-700">{alerta.funcionario_nome}</span>
                  <Badge variant="outline" className="text-amber-700 border-amber-300">
                    {alerta.periodo} — Saldo: {formatarHoras(alerta.saldo_atual)}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {dashboard.funcionarios && dashboard.funcionarios.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Funcionários por Saldo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 font-medium">Funcionário</th>
                    <th className="pb-2 font-medium">Empresa</th>
                    <th className="pb-2 font-medium text-right">Saldo</th>
                    <th className="pb-2 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.funcionarios.map((f) => (
                    <tr key={f.funcionario_id} className="border-b last:border-0">
                      <td className="py-2">{f.funcionario_nome}</td>
                      <td className="py-2 text-gray-500">{f.empresa_nome || '-'}</td>
                      <td className={`py-2 text-right font-medium ${f.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatarHoras(f.saldo)}
                      </td>
                      <td className="py-2 text-right">
                        <Badge variant={f.status === 'positivo' ? 'outline' : 'destructive'} className="text-xs">
                          {f.status === 'positivo' ? 'Crédito' : 'Débito'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BancoHorasDashboard;
